(() => {
  'use strict';
  if (customElements.get('gc-booking-builder')) return;

  class GCBookingBuilder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.frame = this.querySelector('iframe');
      this.status = this.querySelector('[data-booking-status]');
      this.loadState = this.querySelector('[data-booking-load-state]');
      this.fallbackLink = this.querySelector('[data-booking-fallback]');
      if (!this.frame) return;
      const bookingURL = this.frame.dataset.bookingSrc;
      this.origin = new URL(bookingURL).origin;
      this.ready = false;
      if (this.loadState) this.loadState.hidden = false;
      if (this.fallbackLink) this.fallbackLink.hidden = true;
      const { signal } = this.controller;
      window.addEventListener('message', event => this.receive(event), { signal });
      this.frame.addEventListener('load', () => this.sendContext(), { signal });
      this.frame.addEventListener('error', () => this.showFallback(), { signal });
      document.addEventListener('visitorConsentCollected', () => this.sendContext(), { signal });
      document.addEventListener('click', event => {
        const link = event.target.closest('a[data-booking-anchor]');
        if (!link || new URL(link.href, window.location.href).hash !== `#${this.id}`) return;
        event.preventDefault();
        this.scrollIntoView({ behavior: this.scrollBehavior(), block: 'start' });
        this.frame.focus({ preventScroll: true });
        window.history.replaceState(null, '', `#${this.id}`);
      }, { signal });
      this.timer = window.setTimeout(() => { if (!this.ready) this.showFallback(); }, 15000);
      // Listen before loading the full homepage flow, so its first resize is received.
      this.frame.src = this.frameURL(bookingURL);
      if (window.Shopify?.loadFeatures && !window.Shopify.customerPrivacy) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], error => {
          if (!error && this.isConnected) this.sendContext();
        });
      }
    }

    scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    privacy() {
      const privacy = window.Shopify?.customerPrivacy;
      return {
        analytics: Boolean(privacy && typeof privacy.analyticsProcessingAllowed === 'function' && privacy.analyticsProcessingAllowed()),
        marketing: Boolean(privacy && typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed())
      };
    }

    frameURL(base) {
      const url = new URL(base);
      const query = new URLSearchParams(window.location.search);
      const permission = this.privacy();
      const keys = [];
      if (permission.analytics) keys.push('utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term');
      if (permission.marketing) keys.push('gclid', 'fbclid', 'ttclid', 'msclkid');
      keys.forEach(key => {
        const value = query.get(key);
        if (value) url.searchParams.set(key, value.slice(0, 500));
      });
      if (permission.analytics) url.searchParams.set('parent_ref', `${window.location.origin}${window.location.pathname}`);
      return url.href;
    }

    sendContext() {
      const allowed = this.privacy().marketing;
      const cookie = name => {
        const pair = document.cookie.split(';').map(value => value.trim()).find(value => value.startsWith(`${name}=`));
        if (!pair) return null;
        try { return decodeURIComponent(pair.slice(name.length + 1)); } catch { return null; }
      };
      const source = new URL(`${window.location.origin}${window.location.pathname}`);
      const clickID = new URLSearchParams(window.location.search).get('fbclid');
      if (allowed && clickID) source.searchParams.set('fbclid', clickID.slice(0, 500));
      // The homepage flow's existing bridge accepts null values when consent is declined.
      this.frame.contentWindow?.postMessage({
        type: 'gc-meta-ctx', fbp: allowed ? cookie('_fbp') : null,
        fbc: allowed ? cookie('_fbc') : null, url: allowed ? source.href : null
      }, this.origin);
    }

    receive(event) {
      if (event.origin !== this.origin || event.source !== this.frame.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== 'object' || Array.isArray(data)) return;
      if ((data.type === 'gc-resize' || data.type === 'gc-ready') && Number.isFinite(data.height) && data.height >= 240 && data.height <= 14000) {
        // This flow reports document.scrollHeight, so extra pixels would cause a resize loop.
        this.frame.style.height = `${Math.ceil(data.height)}px`;
        if (!this.ready) {
          this.ready = true;
          this.dataset.ready = 'true';
          window.clearTimeout(this.timer);
          delete this.dataset.fallback;
          if (this.fallbackLink === document.activeElement) this.frame.focus({ preventScroll: true });
          if (this.fallbackLink) this.fallbackLink.hidden = true;
          if (this.loadState) this.loadState.hidden = true;
          this.status.textContent = '';
          this.sendContext();
        }
      }
      if (data.type === 'gc-meta-request') this.sendContext();
      if (data.type === 'gc-scroll-to' && Number.isFinite(data.offset) && data.offset >= 0 && data.offset <= this.frame.getBoundingClientRect().height) {
        window.scrollTo({ top: this.frame.getBoundingClientRect().top + window.scrollY + data.offset - 16, behavior: this.scrollBehavior() });
      }
      if (data.type === 'gc-navigate' && typeof data.url === 'string') {
        let url;
        try { url = new URL(data.url, this.origin); } catch { return; }
        const website = url.protocol === 'https:' && [this.origin, 'https://graveclean.co.uk', 'https://www.graveclean.co.uk', 'https://graveclean.com', 'https://www.graveclean.com'].includes(url.origin);
        const live = url.href === 'https://www.tiktok.com/@graveclean/live';
        if (!website && !live && !['tel:', 'mailto:'].includes(url.protocol)) return;
        if (data.target === '_blank') window.open(url.href, '_blank', 'noopener,noreferrer');
        else window.location.assign(url.href);
      }
      // Quote submissions and paid orders must be confirmed by their owning systems.
      // Iframe loads, resizes and navigation never emit Lead or Purchase events here.
    }

    showFallback() {
      this.ready = false;
      delete this.dataset.ready;
      this.dataset.fallback = 'true';
      if (this.loadState) this.loadState.hidden = false;
      if (this.fallbackLink) this.fallbackLink.hidden = false;
      this.status.textContent = 'The form hasn’t loaded. Open it in a new tab, or call 0330 043 0456 for help.';
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.controller?.abort();
      this.controller = null;
    }
  }
  customElements.define('gc-booking-builder', GCBookingBuilder);
})();

// Keep native disclosure semantics; enhance only the short open/close transition.
(() => {
  'use strict';
  function initialiseBookingFAQs(root = document) {
    root.querySelectorAll('.gcb-faq details').forEach(details => {
      const summary = details.querySelector('summary');
      const answer = details.querySelector('.gcb-faq-answer');
      if (!summary || !answer || !details.animate || details.dataset.faqMotion) return;
      details.dataset.faqMotion = 'true';
      const panel = document.createElement('div');
      panel.className = 'gcb-faq-panel';
      answer.before(panel);
      panel.append(answer);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let animation = null;
      let expanded = details.open;

      const finish = () => {
        const current = animation;
        animation = null;
        if (current) current.onfinish = null;
        details.open = expanded;
        panel.style.height = '';
        panel.style.overflow = '';
        current?.cancel();
      };

      summary.addEventListener('click', event => {
        if (event.defaultPrevented || reduceMotion.matches) return;
        event.preventDefault();
        // Start reversals at the currently displayed height, not the last endpoint.
        const from = details.open ? panel.getBoundingClientRect().height : 0;
        expanded = animation ? !expanded : !details.open;
        if (animation) {
          animation.onfinish = null;
          animation.cancel();
        }
        panel.style.height = `${from}px`;
        panel.style.overflow = 'hidden';
        details.open = true;
        const to = expanded ? answer.getBoundingClientRect().height : 0;
        const current = panel.animate(
          [{ height: `${from}px` }, { height: `${to}px` }],
          { duration: 200, easing: 'cubic-bezier(0.23, 1, 0.32, 1)', fill: 'forwards' }
        );
        animation = current;
        current.onfinish = () => { if (animation === current) finish(); };
      });
      reduceMotion.addEventListener('change', () => {
        if (reduceMotion.matches && animation) finish();
      });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseBookingFAQs(), { once: true });
  } else initialiseBookingFAQs();
  document.addEventListener('shopify:section:load', event => initialiseBookingFAQs(event.target));
})();

// Native horizontal gallery; no automatic movement and no changes to the booking frame.
(() => {
  'use strict';
  if (customElements.get('gc-finished-gallery')) return;
  class GCFinishedGallery extends HTMLElement {
    connectedCallback() {
      if (this.galleryController) return;
      this.track = this.querySelector('[data-gallery-track]');
      this.controls = this.querySelector('[data-gallery-controls]');
      this.previous = this.querySelector('[data-gallery-previous]');
      this.next = this.querySelector('[data-gallery-next]');
      if (!this.track || !this.controls || !this.previous || !this.next) return;
      this.galleryController = new AbortController();
      const { signal } = this.galleryController;
      this.previous.addEventListener('click', () => this.move(-1), { signal });
      this.next.addEventListener('click', () => this.move(1), { signal });
      this.track.addEventListener('scroll', () => this.updateControls(), { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        if (event.altKey || event.ctrlKey || event.metaKey) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') {
          this.track.scrollTo({ left: event.key === 'Home' ? 0 : this.track.scrollWidth, behavior: 'auto' });
        } else this.move(event.key === 'ArrowLeft' ? -1 : 1, true);
      }, { signal });
      if (typeof ResizeObserver !== 'undefined') {
        this.galleryResize = new ResizeObserver(() => this.updateControls());
        this.galleryResize.observe(this.track);
      } else window.addEventListener('resize', () => this.updateControls(), { signal });
      this.updateControls();
    }

    updateControls() {
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.controls.hidden = maximum <= 2;
      this.previous.setAttribute('aria-disabled', String(this.track.scrollLeft <= 2));
      this.next.setAttribute('aria-disabled', String(this.track.scrollLeft >= maximum - 2));
    }

    move(direction, immediate = false) {
      const control = direction < 0 ? this.previous : this.next;
      if (control.getAttribute('aria-disabled') === 'true') return;
      const left = this.track.scrollLeft;
      const edge = this.track.getBoundingClientRect().left;
      const positions = [...this.track.querySelectorAll('figure')].map(item => item.getBoundingClientRect().left - edge + left);
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const target = direction > 0
        ? positions.find(position => position > left + 2) ?? maximum
        : positions.reverse().find(position => position < left - 2) ?? 0;
      this.track.scrollTo({
        left: Math.max(0, Math.min(maximum, target)),
        behavior: immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    disconnectedCallback() {
      this.galleryController?.abort();
      this.galleryResize?.disconnect();
      this.galleryController = null;
    }
  }
  customElements.define('gc-finished-gallery', GCFinishedGallery);
})();
